import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import EditorToolbar from "../rich-editor/EditorToolbar";
import SelectionToolbar from "../rich-editor/SelectionToolbar";
import { storageToHtml, htmlToStorage, countPlainChars } from "../rich-editor/richEditorUtils";

/**
 * Rich text editor for question answer text areas.
 *
 * Exposes an imperative handle so Question.js can insert voice-to-text results:
 *   editorRef.current.insertText(text)   — appends text at the current cursor
 *   editorRef.current.getPlainText()     — returns raw text (no tags) for prefix detection
 */
const RichQuestionEditor = forwardRef(function RichQuestionEditor(
  { value, onChange, onCharCount, disabled, maxLength, id, className },
  ref
) {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        code: false,
        horizontalRule: false,
      }),
      TextStyle,
      Color,
      FontSize,
    ],
    content: storageToHtml(value),
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (!onChange) return;
      const plainCount = countPlainChars(editor.getHTML());
      if (onCharCount) onCharCount(plainCount);
      isInternalUpdate.current = true;
      onChange(htmlToStorage(editor.getHTML()));
    },
  });

  // Sync external value changes (e.g. voice-to-text result written via onResult)
  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const incoming = storageToHtml(value);
    if (editor.getHTML() !== incoming) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  // Keep editable state in sync with disabled prop
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  // Expose imperative methods so Question.js can drive voice-to-text insertion
  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      if (!editor) return;
      editor.chain().focus().insertContent(text).run();
    },
    getPlainText: () => {
      if (!editor) return "";
      return editor.getText();
    },
  }));

  const isOverLimit = maxLength && countPlainChars(editor?.getHTML() ?? "") >= maxLength;

  return (
    <div
      className={`rich-question-editor-wrapper ${isOverLimit ? "rich-question-editor--warning" : ""} ${className || ""}`}
      id={id}
    >
      <SelectionToolbar editor={editor} disabled={disabled} />
      <EditorToolbar editor={editor} />
      <EditorContent
        editor={editor}
        className="rich-question-editor-content form-control shadow-none"
      />
    </div>
  );
});

export default RichQuestionEditor;
